import { createElement, getUiRoot, setText } from './dom';
import { setLabel } from './i18n';
import { createIcon } from './icons';
import type { DiscoveryPlace } from '../discovery/types';

/** How long a newly found place is told before the next one, or the city, has the screen. */
const SHOWN_MS = 15_000;

function layout() {
  const root = createElement('section', 'discovery-card', getUiRoot());
  root.hidden = true;
  root.setAttribute('role', 'status');
  const header = createElement('div', 'discovery-card-header', root);
  const label = createElement('p', 'discovery-card-label', header);
  setText(label, 'You found a new place');
  const close = createElement('button', 'discovery-card-close', header);
  close.type = 'button';
  setLabel(close, 'aria-label', 'Close');
  setLabel(close, 'title', 'Close');
  const icon = createIcon('close', 16);
  icon.setAttribute('aria-hidden', 'true');
  close.append(icon);
  const name = createElement('h2', 'discovery-card-name', root);
  const text = createElement('p', 'discovery-card-text', root);
  const more = createElement('button', 'discovery-card-more', root);
  more.type = 'button';
  setText(more, 'Open the discovery book');
  return { root, close, name, text, more };
}

/** The moment of finding a place: its name and what it is, over the city, without stopping the
 *  visitor. Places found together are told one after another. */
export function createDiscoveryCard(open: (place: DiscoveryPlace) => void) {
  const { root, close, name, text, more } = layout();
  const waiting: DiscoveryPlace[] = [];
  let shown: DiscoveryPlace | null = null;
  let until = 0;
  const next = (now: number): void => {
    shown = waiting.shift() ?? null;
    root.hidden = !shown;
    if (!shown) return;
    until = now + SHOWN_MS;
    setText(name, shown.name);
    setText(text, shown.text);
  };
  close.addEventListener('click', () => {
    next(performance.now());
  });
  more.addEventListener('click', () => {
    const place = shown;
    waiting.length = 0;
    next(performance.now());
    if (place) open(place);
  });
  return {
    get current(): string | null {
      return shown?.id ?? null;
    },
    show(place: DiscoveryPlace): void {
      waiting.push(place);
    },
    /** The finds were the account's, and leave with it. */
    clear(): void {
      waiting.length = 0;
      next(performance.now());
    },
    update(now: number, visible: boolean): void {
      if (!shown || now >= until) next(now);
      root.classList.toggle('discovery-card-away', !visible);
    },
    dispose(): void {
      root.remove();
    },
  };
}
